import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { LoadingState, MessageState } from "../interfaces/all";

interface UseChatScrollProps {
  selectedConversation: any;
  onError: (error: any) => void;
  onLoadMessages: (
    phone: string,
    page?: number,
    append?: boolean,
  ) => Promise<void>;
}

interface UseChatScrollReturn {
  messageState: MessageState;
  setMessageState: (
    state: MessageState | ((prev: MessageState) => MessageState),
  ) => void;
  loadMoreRef: any;
  inView: boolean;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottom: (behavior?: "auto" | "smooth") => void;
  isLoading: boolean;
  loadMoreMessages: () => Promise<void>;
}

export const useChatScroll = ({
  selectedConversation,
  onError,
  onLoadMessages,
}: UseChatScrollProps): UseChatScrollReturn => {
  const [messageState, setMessageState] = useState<MessageState>(() => ({
    loadingState: "initial" as LoadingState,
    hasMore: true,
    conversation: null,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollHeightRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  const isMountedRef = useRef(true);
  const isLoadingMoreRef = useRef(false);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: "100px 0px 0px 0px",
  });

  const getCurrentPhone =
    selectedConversation?.valid_telephone ||
    selectedConversation?.telephone ||
    selectedConversation?.phone ||
    null;

  console.log(
    "[DEBUG] useChatScroll -> selectedConversation.telephone:",
    selectedConversation?.telephone,
  );
  console.log(
    "[DEBUG] useChatScroll -> selectedConversation.valid_telephone:",
    selectedConversation?.valid_telephone,
  );
  console.log(
    "[DEBUG] useChatScroll -> selectedConversation.phone:",
    selectedConversation?.phone,
  );
  console.log("[DEBUG] useChatScroll -> getCurrentPhone:", getCurrentPhone);

  const scrollToBottom = useCallback(
    (behavior: "auto" | "smooth" = "smooth") => {
      if (!wrapperRef.current) return;

      // Usar requestAnimationFrame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        if (wrapperRef.current) {
          // Scroll para o final do container
          wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;

          // Fallback adicional usando scrollIntoView se disponível
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: behavior,
              block: "end",
              inline: "nearest",
            });
          }
        }
      });
    },
    [],
  );

  // Função para carregar mais mensagens
  const loadMoreMessages = useCallback(async () => {
    if (
      !getCurrentPhone ||
      isLoadingMoreRef.current ||
      !hasMore ||
      !isMountedRef.current
    ) {
      return;
    }

    try {
      isLoadingMoreRef.current = true;
      setMessageState((prev) => ({
        ...prev,
        loadingState: "loading_more" as LoadingState,
      }));

      const nextPage = currentPage + 1;

      await onLoadMessages(getCurrentPhone, nextPage, true);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error(
        "[DEBUG] useChatScroll - erro ao carregar mais mensagens:",
        error,
      );
      if (onError) {
        onError(error);
      }
    } finally {
      isLoadingMoreRef.current = false;
      setMessageState((prev) => ({
        ...prev,
        loadingState: "idle" as LoadingState,
      }));
    }
  }, [getCurrentPhone, currentPage, hasMore, onLoadMessages, onError]);

  // Efeito para carregar mais mensagens quando inView é true
  useEffect(() => {
    if (
      inView &&
      hasMore &&
      !isLoadingMoreRef.current &&
      messageState.loadingState === "idle"
    ) {
      loadMoreMessages();
    }
  }, [inView, hasMore, loadMoreMessages, messageState.loadingState]);

  // Efeito para atualizar hasMore quando messageState.hasMore muda
  useEffect(() => {
    if (messageState.hasMore !== hasMore) {
      setHasMore(messageState.hasMore);
    }
  }, [messageState.hasMore, hasMore]);

  // Efeito para garantir que isMountedRef está correto
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Efeito para carregar mensagens quando a conversa muda
  useEffect(() => {
    console.log(
      "[DEBUG] useChatScroll -> Efeito de carregar mensagens acionado!",
      {
        getCurrentPhone,
        isMountedRef: isMountedRef.current,
        selectedConversationId: selectedConversation?.id,
      },
    );

    if (!getCurrentPhone || !isMountedRef.current) {
      console.log(
        "[DEBUG] useChatScroll -> EARLY RETURN: getCurrentPhone nulo ou não montado!",
      );
      return;
    }

    // Resetar estado de paginação quando muda de conversa
    setCurrentPage(1);
    setHasMore(true);
    isLoadingMoreRef.current = false;

    // Resetar estado de carregamento
    setMessageState((prev) => ({
      ...prev,
      loadingState: "initial" as LoadingState,
      hasMore: true,
    }));

    // Iniciar carregamento
    if (onLoadMessages) {
      onLoadMessages(getCurrentPhone, 1, false);
    }
  }, [getCurrentPhone, onLoadMessages]);

  // Efeito para scroll inicial
  useEffect(() => {
    if (isInitialLoadRef.current && isMountedRef.current) {
      isInitialLoadRef.current = false;
      scrollToBottom("auto");
    }
  }, [scrollToBottom]);

  // Efeito para scroll ao mudar de conversa
  useEffect(() => {
    if (getCurrentPhone && isMountedRef.current) {
      // Resetar flag para permitir scroll inicial
      isInitialLoadRef.current = true;
      scrollHeightRef.current = 0;

      // Scroll instantâneo para a última mensagem
      setTimeout(() => {
        scrollToBottom("auto");
      }, 100);
    }
  }, [getCurrentPhone, scrollToBottom]);

  // Cleanup quando o componente for desmontado
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    messageState,
    setMessageState,
    loadMoreRef,
    inView,
    wrapperRef,
    messagesEndRef,
    scrollToBottom,
    isLoading,
    loadMoreMessages,
  };
};
